import type {
  AcknowledgementLogPage,
  AcknowledgementPage,
  AcknowledgementSubject,
  AcknowledgementWrite,
  AcknowledgementWriteResult,
} from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

/** One delta page request against a subject's acknowledgement log: everything created after `since`, oldest first. */
export type AcknowledgementLogQuery = {
  workspaceId: string;
  subject: AcknowledgementSubject;
  since: string;
  limit?: number;
  cursor?: string;
};

/** Reads one page of a workspace's acknowledgement log for one subject. */
export function listAcknowledgementLog(
  transport: Transport,
  query: AcknowledgementLogQuery,
): Promise<AcknowledgementPage> {
  return operations.getWorkspacesAcknowledgements(transport, query);
}

/**
 * One page request against a subject's cursor-paged acknowledgement log.
 *
 * A walk's first request may send `since`, or nothing, and the server floors it at the lookback; every later request
 * sends only the `cursor` the previous answer carried.
 */
export type AcknowledgementLogPageQuery = {
  workspaceId: string;
  /** A subject the route serves, as the spec lists them. */
  subject: Parameters<typeof operations.getWorkspacesAcknowledgementLog>[1]["subject"];
  since?: string;
  cursor?: string;
  limit: number;
};

/**
 * Reads one page of a subject's cursor-paged acknowledgement log, oldest first.
 *
 * A cursor the server refuses answers 400, and the caller drops it and cold-starts.
 */
export function listAcknowledgementLogPage(
  transport: Transport,
  query: AcknowledgementLogPageQuery,
): Promise<AcknowledgementLogPage> {
  return operations.getWorkspacesAcknowledgementLog(transport, query);
}

/**
 * Records a batch of one subject's acknowledgements.
 *
 * Idempotent on each write's `id`, so a retry is safe; every write must be of `subject`.
 */
export function writeAcknowledgementLog(
  transport: Transport,
  args: { workspaceId: string; subject: AcknowledgementSubject; acknowledgements: AcknowledgementWrite[] },
): Promise<AcknowledgementWriteResult> {
  return operations.postWorkspacesAcknowledgements(transport, {
    workspaceId: args.workspaceId,
    subject: args.subject,
    body: { acknowledgements: args.acknowledgements },
  });
}
